import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import { Reveal } from "../components/Reveal";
import { Trophy, Clock, Target, Flame, Search, EyeOff, Gamepad2, Medal, FileText, Coins } from "lucide-react";
import { BadgeIcon, SubBadge } from "../lib/badges";
import { StaffName } from "../components/StaffName";

const ROLE_COLORS = { lonestar: "#00A3FF", valkyra: "#FF3B30", manticore: "#34C759" };

const SORTS = [
  { id: "ore", label: "Ore di gioco", icon: Clock },
  { id: "obiettivi", label: "Achievement", icon: Target },
  { id: "fedelta", label: "Badge fedeltà", icon: Medal },
];

function sortKey(p, sort) {
  if (sort === "obiettivi") return -(p.achievements_unlocked || 0);
  if (sort === "fedelta") return -(p.visits || 0);
  return -(p.playtime_hours || 0);
}

export default function Classifica() {
  const { t } = useLang();
  const [players, setPlayers] = useState(null);
  const [error, setError] = useState(false);
  const [sort, setSort] = useState("ore");
  const [query, setQuery] = useState("");
  const [report, setReport] = useState(null);
  const [playing, setPlaying] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    const loadBoard = () => api.get("/leaderboard").then(({ data }) => {
      setPlayers(data.players);
      setUpdatedAt(data.updated_at ? new Date(data.updated_at * 1000) : new Date());
    }).catch(() => setError(true));
    loadBoard();
    api.get("/war-report").then(({ data }) => setReport(data.report)).catch(() => {});
    const loadPulse = () => api.get("/game/pulse").then(({ data }) => setPlaying((data.members || []).map((m) => m.login))).catch(() => {});
    loadPulse();
    const iv = setInterval(loadPulse, 300000);
    const ivBoard = setInterval(loadBoard, 300000);
    return () => { clearInterval(iv); clearInterval(ivBoard); };
  }, []);

  const visible = useMemo(() => {
    if (!players) return [];
    const q = query.trim().toLowerCase();
    return [...players]
      .sort((a, b) => sortKey(a, sort) - sortKey(b, sort))
      .filter((p) => !q || p.display_name.toLowerCase().includes(q) || p.login.toLowerCase().includes(q));
  }, [players, sort, query]);

  const rankOf = (p) => visible.indexOf(p) + 1;

  return (
    <main className="pt-32 pb-28" data-testid="classifica-page">
      <div className="max-w-4xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3 text-wardogs">// CLASSIFICA COMMUNITY</p>
          <h1 className="font-display text-6xl uppercase leading-[0.9] mb-4">
            I migliori <span className="text-wardogs">operatori</span>
          </h1>
          <p className="text-steel max-w-xl mb-10">
            La classifica degli operatori della community con Steam collegato: ore su WARDOGS, obiettivi sbloccati e badge di fedeltà. Collega Steam dal tuo profilo per entrare in graduatoria. I dati Steam si aggiornano automaticamente ogni pochi minuti.
          </p>
        </Reveal>

        {/* RAPPORTO DI GUERRA settimanale */}
        {report && (
          <Reveal>
            <div className="chamfer border border-wardogs/40 bg-wardogs/5 p-6 mb-10" data-testid="war-report-card">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-5 h-5 text-wardogs" />
                <h2 className="font-display text-2xl uppercase">{t("wr.title")} <span className="text-steel">· {t("wr.week")} {report.week}</span></h2>
              </div>
              <div className="flex flex-wrap gap-x-10 gap-y-2 mb-4">
                <p className="font-mono text-sm"><span className="text-wardogs font-bold text-xl">{report.total_hours}h</span> <span className="text-steel text-xs">{t("wr.hours")}</span></p>
                <p className="font-mono text-sm"><span className="text-white font-bold text-xl">{report.active_players}</span> <span className="text-steel text-xs">{t("wr.active")}</span></p>
              </div>
              <div className="space-y-1.5 mb-3">
                {report.lines.map((l, i) => (
                  <p key={i} className="font-mono text-xs text-white" dangerouslySetInnerHTML={{ __html: l.replace(/\*\*(.+?)\*\*/g, '<span class="text-wardogs font-bold">$1</span>') }} />
                ))}
              </div>
              <p className="font-mono text-[11px] text-steel">{t("wr.climbers")}: <span dangerouslySetInnerHTML={{ __html: report.climb_txt.replace(/\*\*(.+?)\*\*/g, '<span class="text-white font-bold">$1</span>') }} /></p>
            </div>
          </Reveal>
        )}

        <Reveal>
          <div className="flex flex-col md:flex-row gap-3 mb-8" data-testid="classifica-controls">
            <div className="flex gap-px bg-[#1A1A1A] border border-[#1A1A1A]">
              {SORTS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSort(s.id)}
                  data-testid={`classifica-sort-${s.id}`}
                  className={`flex items-center gap-2 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors duration-300 ${
                    sort === s.id ? "bg-wardogs text-[#050505] font-bold" : "bg-[#050505] text-steel hover:text-white"
                  }`}
                >
                  <s.icon className="w-3.5 h-3.5" /> {s.label}
                </button>
              ))}
            </div>
            <div className="flex-1 relative">
              <Search className="w-4 h-4 text-steel absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca operatore…"
                data-testid="classifica-search"
                className="w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none pl-11 pr-4 py-2.5 font-mono text-sm text-white transition-colors duration-300"
              />
            </div>
          </div>
        </Reveal>

        {error && (
          <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-8 text-center" data-testid="classifica-error">
            <p className="font-mono text-xs text-steel">La classifica non si carica ora. Riprova tra poco.</p>
          </div>
        )}

        {!players && !error && (
          <div className="flex flex-col gap-3" data-testid="classifica-loading">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-5 animate-pulse flex gap-4 items-center">
                <div className="w-10 h-10 bg-[#1A1A1A]" />
                <div className="h-4 w-48 bg-[#1A1A1A]" />
              </div>
            ))}
          </div>
        )}

        {players && (
          <div className="flex flex-col gap-3" data-testid="classifica-list">
            {visible.map((p) => {
              const rank = rankOf(p);
              const color = ROLE_COLORS[p.role] || "#8A8D91";
              const top3 = rank <= 3;
              return (
                <Reveal key={p.login} delay={Math.min(rank * 0.03, 0.3)}>
                  <Link
                    to={`/operatore/${p.login}`}
                    className={`chamfer border bg-[#0C0C0C] px-5 py-4 flex items-center gap-4 transition-colors duration-300 group ${
                      top3 ? "border-wardogs/50" : "border-[#1A1A1A] hover:border-wardogs/50"
                    }`}
                    data-testid={`classifica-row-${p.login}`}
                  >
                    <span className={`font-display text-3xl w-12 shrink-0 ${top3 ? "text-wardogs" : "text-[#2A2A2A]"}`} data-testid={`classifica-rank-${p.login}`}>
                      {String(rank).padStart(2, "0")}
                    </span>
                    {p.avatar && <img src={p.avatar} alt="" className="w-10 h-10 chamfer-sm border border-[#1A1A1A] shrink-0" loading="lazy" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {p.is_staff ? (
                          <StaffName name={p.display_name} className="font-mono text-sm font-bold truncate" />
                        ) : (
                          <p className="font-mono text-sm text-white font-bold truncate group-hover:text-wardogs transition-colors duration-300">{p.display_name}</p>
                        )}
                        {p.subscribed && <SubBadge tier={p.sub_tier} testid={`classifica-sub-${p.login}`} />}
                        {playing.includes(p.login) && (
                          <span className="flex items-center gap-1.5 shrink-0" title="In partita su WARDOGS ora" data-testid={`playing-now-${p.login}`}>
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#23A55A] opacity-70" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#23A55A]" />
                            </span>
                            <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-[#23A55A] hidden sm:inline">in partita ora</span>
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {p.role && (
                          <span className="font-mono text-[9px] uppercase tracking-[0.15em]" style={{ color }}>{p.role}</span>
                        )}
                        {p.private && (
                          <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.12em] text-[#4A4D51]">
                            <EyeOff className="w-3 h-3" /> profilo Steam privato
                          </span>
                        )}
                        {p.owns_wardogs === false && !p.private && (
                          <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-[#4A4D51]">non possiede il gioco</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-5 shrink-0">
                      <span className="flex items-center gap-1.5 font-mono text-xs text-white" title="Ore su WARDOGS">
                        <Clock className="w-3.5 h-3.5 text-wardogs" />
                        {p.playtime_hours != null ? `${p.playtime_hours}h` : "—"}
                      </span>
                      <span className="hidden sm:flex items-center gap-1.5 font-mono text-xs text-white" title="Achievement sbloccati">
                        <Target className="w-3.5 h-3.5 text-[#00A3FF]" />
                        {p.achievements_unlocked != null ? `${p.achievements_unlocked}${p.achievements_total ? `/${p.achievements_total}` : ""}` : "—"}
                      </span>
                      <span className="hidden md:flex items-center gap-1.5 font-mono text-xs text-steel" title="Badge fedeltà">
                        <BadgeIcon visits={p.visits} testid={`classifica-badge-${p.login}`} />
                      </span>
                      {(p.streak || 0) > 0 && (
                        <span className="hidden md:flex items-center gap-1 font-mono text-xs text-steel" title="Serie di accessi">
                          <Flame className="w-3.5 h-3.5 text-wardogs" /> {p.streak}
                        </span>
                      )}
                    </div>
                  </Link>
                </Reveal>
              );
            })}
            {visible.length === 0 && (
              <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-10 text-center" data-testid="classifica-empty">
                <Gamepad2 className="w-8 h-8 text-[#2A2A2A] mx-auto mb-4" />
                <p className="font-mono text-xs text-steel leading-relaxed">
                  {query ? "Nessun operatore trovato con questo nome." : "Nessun operatore ha ancora collegato Steam. Sii il primo: collega Steam dal tuo profilo e conquista la vetta."}
                </p>
              </div>
            )}
          </div>
        )}

        <Reveal>
          <div className="chamfer border border-dashed border-wardogs/40 bg-[#0C0C0C] p-6 mt-10" data-testid="wealth-teaser">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <Coins className="w-5 h-5 text-wardogs" />
              <h2 className="font-display text-2xl uppercase">Classifica <span className="text-wardogs">Ricchezza</span></h2>
              <span className="ml-auto font-mono text-[9px] uppercase tracking-[0.2em] text-wardogs border border-wardogs/40 px-2 py-1" data-testid="wealth-teaser-badge">In arrivo</span>
            </div>
            <p className="text-steel text-sm max-w-xl mb-5">
              Chi accumulerà più crediti in WARDOGS? La graduatoria degli operatori più ricchi della community si attiverà automaticamente qui appena BULKHEAD renderà disponibili i dati di gioco via API.
            </p>
            <div className="flex flex-col gap-2" data-testid="wealth-teaser-ghosts">
              {[1, 2, 3].map((r) => (
                <div key={r} className="chamfer border border-[#1A1A1A] bg-[#050505] px-5 py-3 flex items-center gap-4 opacity-60">
                  <span className="font-display text-2xl w-10 text-[#2A2A2A]">{String(r).padStart(2, "0")}</span>
                  <span className="flex-1 font-mono text-sm text-[#2A2A2A] tracking-[0.3em]">???</span>
                  <span className="font-mono text-xs text-[#2A2A2A]">—</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal>
          <p className="font-mono text-[10px] text-[#4A4D51] mt-10 flex items-center gap-2 flex-wrap">
            <Trophy className="w-3.5 h-3.5" /> Dati pubblici Steam, aggiornati automaticamente ogni pochi minuti.
            {updatedAt && (
              <span className="text-steel" data-testid="classifica-updated-at">
                Ultimo aggiornamento: {updatedAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
            )}
            K/D e statistiche di combattimento arriveranno appena BULKHEAD le renderà disponibili via API.
          </p>
        </Reveal>
      </div>
    </main>
  );
}
